import React from "react";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import type Page from "../models/Page";
import ConnectorGrab from "../components/ConnectorGrab";
import type Button from "../models/Button";
import type Channel from "../models/Channel";
import { ConnectionType, dehighlightConnections, highlightConnections } from "../models/Connection";
import { channelTypeInfo, getChannelTypePicture } from "../models/ChannelType";
import ConnectableObject from "../models/ConnectableObject";
import ConnectionTarget from "./ConnectionTarget";

import ObjectReference from "../components/ObjectReference";
import type BaseModel from "../models/BaseModel";
import EditableText from "../components/EditableText";
import type Room from "../models/Room";
import type Floor from "../models/Floor";

export default function ButtonChannelView(props: { mainPage: Page; page: Page; button: Button; channel: Channel }) {
  const { mainPage, page, button, channel } = props;

  useOnChangeWatcher(channel);

  const isNewButtonChannel = channel.tag == "@NEWC" || channel.tag == "@NEW";

  const knxChannel = channel.knxChannel;
  const room = button.parent;

  var name = channel.name;
  if (knxChannel) {
    name = knxChannel.name;
  }
  const tag = channel.tag;

  const dropTargetRef = React.useRef<HTMLDivElement>(null);

  function onDragEnter() {
    if (dropTargetRef.current) {
      dropTargetRef.current.style.backgroundColor = "var(--primary-color-light)";
    }
  }

  function onDragLeave() {
    if (dropTargetRef.current) {
      (dropTargetRef.current.style.backgroundColor as any) = "initial";
    }
  }

  const style: any = {
    position: "relative",
    // marginLeft: "5px",
    // marginRight: "5px",
    height: "max-content",

    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  };

  if (channel.tag.endsWith("R")) {
    // no itx
    style.marginBottom = "10px"; // no itx
  }

  let styleInnerButton: any = {
    width: "100%",
    minWidth: "fit-content",
    alignItems: "center",
    height: "25px",
    display: "flex",
    flexDirection: "row",
    borderRadius: "20px",
    paddingLeft: "10px",
    paddingRight: "10px",
    border: "0px",
    boxShadow: "var(--panel-shadow)",
  };

  if (channel.tag.includes("L")) {
    styleInnerButton = {
      ...styleInnerButton,
      borderRadius: "20px 5px 5px 20px",
      paddingLeft: "10px",
    };
  } else if (channel.tag.includes("R")) {
    styleInnerButton = {
      ...styleInnerButton,
      borderRadius: "5px 20px 20px 5px",
      paddingRight: "10px",
    };
  }

  if (channel.hasErrors) {
    styleInnerButton.backgroundColor = "var(--error-color)";
  }

  if (channel.selected) {
    styleInnerButton.backgroundColor = "var(--panel-background-selected)";
  }

  const partnersInOtherRoomElements = new Array<any>();
  const partnersInOtherRoom = new Array<ConnectableObject>();

  const isFloorPlan = page.isFloorPlan;
  channel.connections.forEach((connection) => {
    const partner = connection.getPartner(channel);
    const partnerRoom = partner?.parent as Room;
    const partnerFloor = partnerRoom?.parent as Floor;

    let isSameParent = false;
    if (isFloorPlan) {
      isSameParent = partnerFloor == page.parentFloor;
    } else {
      isSameParent = partnerRoom == page.parentRoom;
    }

    if (partner?.parent && !isSameParent && partner.className != "Scene" && partner.className != "Multiplexer") {
      partnersInOtherRoom.push(partner);
    }
  });

  partnersInOtherRoom.forEach((partner) => {
    partnersInOtherRoomElements.push(
      <ObjectReference
        key={partner.id}
        style={{ height: "20px", fontSize: "0.7em", paddingRight: "0px" }}
        unlinkButtonStyle={{ position: "absolute", right: "-5px ", top: "-23px", zIndex: "20" }}
        obj={partner}
        showName={channel.connections.size > 1}
        useAllwaysMaxSpace={true}
        relative={button.parent as BaseModel}
        onUnlink={() => {
          channel.unlinkConnectionWith(partner as ConnectableObject);
        }}
      />,
    );
  });

  let channelName = <EditableText obj={channel} property="nameOrConnectedNames" disableEdit={!channel.selected} />;

  // if (channel.isActorChannel) {
  //   styleInnerButton.borderTopLeftRadius = "0px";
  //   styleInnerButton.borderBottomLeftRadius = "0px";

  //   // style.borderLeft = "3px solid grey";
  //   style.borderTopLeftRadius = "0px";
  //   style.borderBottomLeftRadius = "0px";
  // }

  let backgroundColor = knxChannel?.isStatusChannel ? "yellow" : "white";
  if (isNewButtonChannel) {
    backgroundColor = "#cfc";
  }

  const knxChannelName = knxChannel?.name;

  return (
    <ConnectionTarget
      className="buttonChannel "
      page={page}
      target={channel}
      compact={2}
      connectionType={channel.isActorChannel ? ConnectionType.Channel : ConnectionType.Button}
      style={style}
      print={undefined}
    >
      <div style={{ position: "relative", paddingBottom: "5px", width: "100%", display: "flex", flexDirection: "row" }}>
        <div
          className="port portL"
          style={{ position: "absolute", top: "50%", left: "0%", width: "1px", height: "1px" }}
        >
          {channelTypeInfo(channel.channelType).canBeConnectedWithPartner == true && (
            <ConnectorGrab
              mainPage={mainPage}
              page={page}
              connectableObject={channel}
              // x={channel.isStatusChannel ? -1 : 100}
              x={0}
              portName={"L"}
              ownerSelected={button.selected}
              direction={180}
              type={ConnectionType.Button}
              disabled={false}
            />
          )}
        </div>

        <div
          id={channel.id}
          ref={dropTargetRef}
          style={{
            position: "relative",
            width: "100%",
            minWidth: "fit-content",
            display: "flex",
            flexDirection: "row",
          }}
          onMouseEnter={(ev: any) => {
            dehighlightConnections(true, page, button);
            highlightConnections(true, page, channel, undefined);
          }}
          onMouseLeave={(ev: any) => {
            dehighlightConnections(true, page, channel, undefined);
          }}
          onMouseMove={(ev: any) => {
            ev.stopPropagation();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={(ev: any) => {
            ev.stopPropagation();
            channel.treeViewModel.onDrop(ev);
            onDragLeave();
          }}
        >
          <div
            style={{
              backgroundColor: backgroundColor,
              borderRadius: "20px",
              position: "relative",
              width: "100%",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <div
              className="hoverable"
              style={styleInnerButton}
              onClick={(ev) => {
                state.selectionManager.selectedObject = channel;

                page.resetIsNewOnAllConnections();
                ev.stopPropagation();
              }}
            >
              {getChannelTypePicture(channel) != "" && (
                <img
                  style={{
                    marginLeft: "0px",
                    marginRight: "10px",
                    height: "15px",
                    alignSelf: "center",
                    opacity: "0.75",
                  }}
                  src={getChannelTypePicture(channel)}
                />
              )}

              {knxChannelName && <div>{knxChannelName} :</div>}

              <div
                style={{
                  display: "inline-block",
                  alignItems: "center",
                  backgroundColor: "var(--colorTransparent)",
                  borderRadius: "5px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  // fontSize: "0.75em",
                }}
              >
                {channelName}
              </div>
            </div>
          </div>
        </div>

        {partnersInOtherRoom.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "10px",
              borderBottom: "3px solid #ccc",
              backgroundColor: "white",
              alignSelf: "center",
            }}
          />
        )}

        {partnersInOtherRoom.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              border: "1px solid #eee",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "3px",
              backgroundColor: "white",
              alignSelf: "center",
              color: "#444",
            }}
          >
            {partnersInOtherRoomElements}
          </div>
        )}

        {partnersInOtherRoom.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "5px",
              borderBottom: "3px solid #ccc",
              backgroundColor: "white",
              alignSelf: "center",
            }}
          />
        )}

        {channelTypeInfo(channel.channelType).canBeConnectedWithPartner == true && (
          <div
            className="port portR"
            style={{ position: "absolute", top: "50%", left: "100%", width: "1px", height: "1px" }}
          >
            <ConnectorGrab
              mainPage={mainPage}
              page={page}
              connectableObject={channel}
              // x={channel.isStatusChannel ? -1 : 100}
              x={100}
              portName="R"
              ownerSelected={button.selected}
              direction={0}
              type={ConnectionType.Button}
              disabled={false}
            />
          </div>
        )}
      </div>
    </ConnectionTarget>
  );
}
